import { Link } from 'react-router-dom';
import { site } from '../config/site';

export type LegalKind = 'privacy' | 'terms' | 'support';

const META: Record<
  LegalKind,
  { title: string; heading: string; description: string }
> = {
  privacy: {
    title: 'Privacy Policy · Workouts',
    heading: 'Privacy Policy',
    description: 'How Workouts collects and uses your information.',
  },
  terms: {
    title: 'Terms of Use · Workouts',
    heading: 'Terms of Use',
    description: 'Terms for using the Workouts fitness coaching app.',
  },
  support: {
    title: 'Support · Workouts',
    heading: 'Support',
    description: 'Help with Workouts subscriptions, sessions, and account.',
  },
};

function PrivacyBody() {
  return (
    <>
      <h2>Who we are</h2>
      <p>
        Workouts is a fitness coaching app operated by{' '}
        <strong>Omnivatis</strong> (“we”, “us”). Contact:{' '}
        <a href={`mailto:${site.supportEmail}`}>{site.supportEmail}</a>.
      </p>

      <h2>Information we collect</h2>
      <ul>
        <li>
          <strong>Contact info</strong> — email and name for your account.
        </li>
        <li>
          <strong>User content</strong> — workout session logs (programs, sets,
          reps, duration); optional avatar or profile photos you upload.
        </li>
        <li>
          <strong>Purchases</strong> — subscription product and entitlement
          dates from Apple App Store / Google Play (we do not store full payment
          card numbers).
        </li>
        <li>
          <strong>Identifiers</strong> — account ID; device / push tokens for
          Firebase Cloud Messaging and analytics.
        </li>
        <li>
          <strong>Usage data</strong> — product events (sign-in, paywall,
          purchase/restore, workout completion) via Firebase Analytics.
        </li>
        <li>
          <strong>Diagnostics</strong> — crash data via Firebase Crashlytics.
        </li>
      </ul>

      <h2>How we use information</h2>
      <ul>
        <li>
          <strong>App functionality</strong> — authenticate you, sync workout
          history, deliver coaching content, and enforce subscription access.
        </li>
        <li>
          <strong>Analytics &amp; reliability</strong> — understand feature use
          and fix crashes.
        </li>
      </ul>

      <h2>AI processing</h2>
      <p>
        Optional coaching prompts may be sent to our AI providers (currently
        OpenAI) for text guidance. Outputs are fitness coaching only — not
        medical advice. Avoid uploading images of others without permission.
      </p>

      <h2>Sharing</h2>
      <p>
        We do not sell your personal information. We share data with processors
        needed to run Workouts (hosting, storage, AI, analytics, push, app
        stores) under appropriate agreements.
      </p>

      <h2>Retention &amp; deletion</h2>
      <p>
        We keep account and workout data while your account is active. You can
        delete your account from Profile. Store subscriptions must be canceled
        separately in Apple / Google settings.
      </p>

      <h2>Children</h2>
      <p>
        Workouts is not directed to children under 13 (or the minimum age
        required in your region).
      </p>

      <h2>Changes</h2>
      <p>
        We may update this policy. The “Last updated” date above will change when
        we do.
      </p>
      <p className="cta-row">
        <Link className="cta" to={site.termsUrl}>
          Terms of Use
        </Link>
        <Link className="cta" to={site.supportUrl}>
          Support
        </Link>
      </p>
    </>
  );
}

function TermsBody() {
  return (
    <>
      <h2>Agreement</h2>
      <p>
        By downloading or using Workouts you agree to these Terms of Use and our{' '}
        <Link to={site.privacyUrl}>Privacy Policy</Link>. If you do not agree,
        do not use the app.
      </p>
      <h2>The service</h2>
      <p>
        Workouts provides guided workout programs with 3D motion coaching, voice
        cues, and session history. Content is for personal fitness support —{' '}
        <strong>not medical diagnosis</strong>, treatment, or physiotherapy
        advice. Consult a professional before starting a new exercise program.
      </p>
      <h2>Accounts &amp; subscriptions</h2>
      <p>
        Access to premium features may require an active subscription. You must
        provide accurate registration details and keep credentials secure.
      </p>
      <h2>Subscriptions</h2>
      <ul>
        <li>
          Premium plans: Monthly and Yearly auto-renewable subscriptions, billed
          through Apple App Store or Google Play. A limited free trial may be
          offered in-app before purchase.
        </li>
        <li>
          Payment is charged at confirmation of purchase (or when a free trial
          converts); subscriptions renew unless canceled at least 24 hours before
          the end of the current period.
        </li>
        <li>
          Manage or cancel in your App Store or Google Play account settings.
          Deleting the app or your Workouts account does not cancel a store
          subscription.
        </li>
        <li>
          Links to these Terms of Use (EULA) and the Privacy Policy are also
          available on the in-app subscription screen. Use Restore purchases if
          you reinstall or switch devices.
        </li>
      </ul>
      <h2>Acceptable use</h2>
      <p>
        Do not misuse Workouts — including unlawful activity or attempting to
        disrupt the service.
      </p>
      <h2>Disclaimer &amp; liability</h2>
      <p>
        The service is provided “as is.” To the fullest extent permitted by law,
        Omnivatis is not liable for indirect or consequential damages arising
        from your use of Workouts, including injury from exercise.
      </p>
      <h2>Contact</h2>
      <p>
        <a href={`mailto:${site.supportEmail}`}>{site.supportEmail}</a>
      </p>
      <p className="cta-row">
        <Link className="cta" to={site.privacyUrl}>
          Privacy Policy
        </Link>
        <Link className="cta" to={site.supportUrl}>
          Support
        </Link>
      </p>
    </>
  );
}

function SupportBody() {
  return (
    <>
      <p>
        Need help with Workouts? Email{' '}
        <a href={`mailto:${site.supportEmail}`}>{site.supportEmail}</a>.
      </p>
      <div className="support-topic">
        <strong>Subscription not unlocking</strong>
        <span>
          Use Restore purchases on the paywall. Confirm you are signed into the
          same Apple / Google account used to buy.
        </span>
      </div>
      <div className="support-topic">
        <strong>Cancel or change plan</strong>
        <span>
          Manage subscriptions in App Store or Google Play account settings.
        </span>
      </div>
      <div className="support-topic">
        <strong>Delete account</strong>
        <span>
          In the app: Profile → Delete account. Or email us from your registered
          address.
        </span>
      </div>
      <h2>Legal</h2>
      <p className="cta-row">
        <Link className="cta" to={site.privacyUrl}>
          Privacy Policy
        </Link>
        <Link className="cta" to={site.termsUrl}>
          Terms of Use
        </Link>
      </p>
    </>
  );
}

export function LegalPage({ kind }: { kind: LegalKind }) {
  const meta = META[kind];

  return (
    <main className="legal-wrap">
      <div className="page-intro">
        <div className="eyebrow">Workouts</div>
        <h1>{meta.heading}</h1>
        <p className="updated">Last updated: August 12, 2026</p>
      </div>
      <article className="legal-article">
        {kind === 'privacy' && <PrivacyBody />}
        {kind === 'terms' && <TermsBody />}
        {kind === 'support' && <SupportBody />}
      </article>
    </main>
  );
}

export function legalDocumentTitle(kind: LegalKind): string {
  return META[kind].title;
}
